<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import TextareaInputAlt from "@/dsComponents/inputs/TextareaInputAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

import workerProfileNoteService from "@/components/modal/Profile/note/services/workerProfileNoteService";
import { ProfileNoteModalMode, type ProfileNoteModalProps } from "@/components/modal/Profile/note/types";

const props = withDefaults(defineProps<ProfileNoteModalProps>(), {
    initialContent: "",
    noteId: undefined,
    onSuccess: undefined,
    onOptimistic: undefined,
});

const emit = defineEmits<{
    close: [];
}>();

const { t } = useI18n();
const toaster = useToast();

const body = ref(props.initialContent ?? "");
const isSaving = ref(false);

const syncBodyFromProps = () => {
    body.value = props.initialContent ?? "";
};

watch(
    () => [props.open, props.initialContent, props.noteId, props.mode] as const,
    ([isOpen]) => {
        if (isOpen) {
            syncBodyFromProps();
        }
    },
    { immediate: true }
);

const badgeLabel = computed(() =>
    props.mode === ProfileNoteModalMode.CREATE ? t("modals.profileNote.badge.create") : t("modals.profileNote.badge.edit")
);

const resolvedNoteId = computed(() => String(props.noteId ?? "").trim());

const isSaveDisabled = computed(() => {
    if (isSaving.value) return true;
    if (!body.value.trim()) return true;
    if (props.mode === ProfileNoteModalMode.EDIT && !resolvedNoteId.value) return true;
    return false;
});

const handleClose = () => {
    emit("close");
};

const handleSave = async () => {
    if (isSaveDisabled.value) return;
    isSaving.value = true;
    try {
        if (props.mode === ProfileNoteModalMode.CREATE) {
            await workerProfileNoteService.createWorkerNote(props.workerId, { content: body.value });
        } else {
            const noteId = resolvedNoteId.value;
            if (!noteId) {
                toaster.open({
                    message: t("modals.profileNote.errors.missingNoteId") as string,
                    type: "error",
                    duration: 4000,
                });
                return;
            }
            props.onOptimistic?.update?.(body.value);
            await workerProfileNoteService.updateWorkerNote(props.workerId, noteId, { content: body.value });
        }
        toaster.open({
            message: t("modals.profileNote.toast.success") as string,
            type: "success",
            duration: 3000,
        });
        props.onSuccess?.();
        handleClose();
    } catch {
        props.onOptimistic?.rollback?.();
        toaster.open({
            message: t("errors.Global.ServerError._default") as string,
            type: "error",
            duration: 4000,
        });
    } finally {
        isSaving.value = false;
    }
};
</script>

<template>
    <Modal class="profile-note-modal" width="720px" height="auto" :open="props.open" close action :z-index-level="1" @close="handleClose">
        <template #header>
            <div class="profile-note-modal__header-inner">
                <div class="profile-note-modal__badge">
                    <i class="ti ti-note profile-note-modal__badge-icon typo-xxs-regular" aria-hidden="true" />
                    <span class="typo-xxs-regular profile-note-modal__badge-label">{{ badgeLabel }}</span>
                </div>
            </div>
        </template>

        <div class="profile-note-modal__body">
            <p v-if="props.mode === ProfileNoteModalMode.EDIT && !resolvedNoteId" class="profile-note-modal__activity-hint typo-xxs-regular">
                {{ t("modals.profileNote.hint.activityMissingNoteId") }}
            </p>
            <TextareaInputAlt
                v-model="body"
                :rows="10"
                size="md"
                no-border
                :placeholder="t('modals.profileNote.fields.content.placeholder')"
                auto-resize
            />
        </div>

        <template #footer>
            <div class="profile-note-modal__footer">
                <ButtonAlt type="neutral" variant="ghost" :label="t('global.actions.cancel')" @click="handleClose" />
                <ButtonAlt
                    variant="filled"
                    :label="t('global.actions.save')"
                    :loading="isSaving"
                    :disabled="isSaveDisabled"
                    @click="handleSave"
                />
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.profile-note-modal {
    &__header-inner {
        display: flex;
        flex: 1 1 auto;
        align-items: center;
        min-height: 28px;
    }

    &__badge {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-sm, 6px);
        padding: var(--spacing-xxs, 2px) var(--spacing-sm, 6px);
        border-radius: 4px;
        background-color: var(--bg-weak-100, #eff2f5);
    }

    &__badge-icon {
        width: 12px;
        height: 12px;
        line-height: 12px;
        color: var(--text-sub-600, #494e61);
    }

    &__badge-label {
        color: var(--text-sub-600, #494e61);
        white-space: nowrap;
    }

    &__body {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        width: 100%;
        min-height: 0;
    }

    &__activity-hint {
        margin: 0 0 var(--spacing-md, 8px);
        color: var(--text-sub-600, #494e61);
    }

    &__footer {
        display: flex;
        width: 100%;
        justify-content: flex-end;
        gap: var(--spacing-lg, 12px);
    }
}
</style>
